import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, makeStyles, tokens } from '@coreai/fluentui-react';
import { useAuth, useAuthStatus } from '../auth';

type ProtectedRouteProps = {
  children: ReactNode;
};

const useStyles = makeStyles({
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

/**
 * Check if MSAL has cached account keys in sessionStorage.
 * This is a lightweight hint that the user was previously authenticated
 * and MSAL just needs a moment to hydrate — don't redirect to /login yet.
 */
function hasCachedMsalSession(): boolean {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes('login.microsoftonline.com') && key.includes('-accesstoken-')) {
        return true;
      }
    }
  } catch { /* sessionStorage unavailable */ }
  return false;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isVerifyingAuth } = useAuthStatus();
  const location = useLocation();
  const styles = useStyles();

  // Show spinner while:
  // - MSAL is verifying the initial session (ssoSilent in flight), or
  // - MSAL is actively processing (login/logout/redirect), or
  // - There's a cached session that hasn't hydrated yet.
  // Without `isVerifyingAuth` we'd flash a redirect to /login during the
  // silent-SSO window even for users who are about to be auto-signed-in.
  if (isVerifyingAuth || isLoading || (!isAuthenticated && hasCachedMsalSession())) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
